import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiX, FiInfo, FiClock, FiPaperclip } from "react-icons/fi";
import DropdownSection from "./DropdownSection";
import FileViewer from "./FileViewer";
import { marked } from "marked";

const renderMarkdown = (markdown = "") => ({
    __html: marked.parse(markdown || ""),
});

// version.files is a jsonb array of { name, url, type } — see the
// app_versions.files column. Rendered as clickable chips that open the
// shared FileViewer in 'external' mode (a plain public URL, no job-scoped
// storage bucket to resolve).
const VersionFiles = ({ files = [], onOpen }) => {
    if (!files.length) return null;

    return (
        <div className="flex flex-wrap gap-2 mt-4">
            {files.map((file, index) => (
                <button
                    key={file.url || index}
                    type="button"
                    onClick={() => onOpen(files, index)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 text-xs font-medium text-gray-700 max-w-[220px]"
                >
                    <FiPaperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="truncate">{file.name || `Attachment ${index + 1}`}</span>
                </button>
            ))}
        </div>
    );
};

const VersionUpdatesModal = ({
    isOpen,
    onClose,
    company,
    latestVersion,
    previousVersions = [],
}) => {
    const [viewerFiles, setViewerFiles] = useState(null);
    const [viewerIndex, setViewerIndex] = useState(0);

    const openViewer = (files, index) => {
        setViewerFiles(files);
        setViewerIndex(index);
    };

    const closeViewer = () => setViewerFiles(null);

    useEffect(() => {
        if (!isOpen) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose();
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const activeFile = viewerFiles?.[viewerIndex] || null;

    const logoSrc =
        company?.subscription_plan === "enterprise" && company?.enterprise_logo_url
            ? company.enterprise_logo_url
            : process.env.PUBLIC_URL + "/assets/wide-logo.svg";

    return createPortal(
        <div className="mobile-dialog-shell fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-3 min-w-0">
                        <img
                            src={logoSrc}
                            alt="Notify"
                            className="h-9 w-auto object-contain"
                        />
                        <div className="min-w-0">
                            <h2 className="text-lg font-bold text-gray-900 truncate">
                                About Notify
                            </h2>
                            <p className="text-sm text-gray-500">
                                Product updates, release notes, and recent changes
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        onTouchEnd={(e) => { e.preventDefault(); onClose(); }}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto px-5 py-5 space-y-4">
                    <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-zinc-50 to-white p-5">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-xs uppercase tracking-wide font-semibold text-gray-500">
                                    Current version
                                </p>
                                <h3 className="text-2xl font-bold text-gray-900 mt-1 mb-2">
                                    {latestVersion?.version || company?.app_version || "—"}
                                </h3>
                                <div className="flex flex-col gap-1">
                                    {latestVersion?.title && (
                                        <p className="text-sm text-gray-600">
                                            {latestVersion.title}
                                        </p>
                                    )}
                                    {latestVersion?.summary && (
                                        <p className="text-sm text-gray-700">
                                            {latestVersion.summary}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <FiClock className="w-4 h-4" />
                                {latestVersion?.created_at
                                    ? new Date(latestVersion.created_at).toLocaleDateString("en-AU", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                    })
                                    : "Latest"}
                            </div>
                        </div>



                        {latestVersion?.content_markdown && (
                            <div
                                className="prose prose-sm max-w-none mt-8 
                                        prose-headings:text-gray-900
                                        prose-p:text-gray-700
                                        prose-li:text-gray-700
                                        prose-strong:text-gray-900
                                        prose-a:text-blue-600
                                        prose-img:rounded-xl
                                        prose-img:shadow-md"
                                dangerouslySetInnerHTML={renderMarkdown(latestVersion.content_markdown)}
                            />
                        )}

                        <VersionFiles files={latestVersion?.files} onOpen={openViewer} />
                    </div>

                    {previousVersions.length > 0 && (
                        <div className="space-y-3">
                            {previousVersions.map((version) => (
                                <DropdownSection
                                    key={version.id}
                                    title={`v${version.version}`}
                                    subtext={
                                        version.created_at
                                            ? new Date(version.created_at).toLocaleDateString("en-AU", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                            })
                                            : ""
                                    }
                                    defaultOpen={false}
                                >
                                    <div className="space-y-2">
                                        {version.title && (
                                            <p className="font-semibold text-gray-800">
                                                {version.title}
                                            </p>
                                        )}
                                        {version.summary && (
                                            <p className="text-sm text-gray-600">
                                                {version.summary}
                                            </p>
                                        )}
                                        <div
                                            className="prose prose-sm max-w-none text-gray-800"
                                            dangerouslySetInnerHTML={renderMarkdown(version.content_markdown)}
                                        />

                                        <VersionFiles files={version.files} onOpen={openViewer} />
                                    </div>
                                </DropdownSection>
                            ))}
                        </div>
                    )}

                    <DropdownSection
                        title="About Notify"
                        icon={FiInfo}
                        defaultOpen={false}
                    >
                        <div className="text-sm text-gray-700 space-y-2">
                            <p>
                                Notify helps your team manage jobs, assignments, schedules,
                                deliveries, and updates in one place.
                            </p>
                            <p>
                                This section shows the latest improvements and the recent
                                release history for your app.
                            </p>
                        </div>
                    </DropdownSection>


                </div>
            </div>

            {activeFile && (
                <FileViewer
                    onClose={closeViewer}
                    file={activeFile}
                    filename={activeFile.url}
                    originalName={activeFile.name}
                    files={viewerFiles}
                    currentIndex={viewerIndex}
                    onNavigate={setViewerIndex}
                    fileType="external"
                />
            )}
        </div>,
        document.body
    );
};

export default VersionUpdatesModal;